/**
 * Engine-neutral presentation contract shared by the Phaser baseline and the
 * P4.20 Babylon feasibility renderer. Gameplay code may describe what an
 * entity is and where it is, but never owns an engine object.
 */
export type WorldEntityKind =
  | "player"
  | "enemy"
  | "elite"
  | "boss"
  | "companion"
  | "resource"
  | "projectile"
  | "core";

export interface WorldEntityDescriptor {
  id: string;
  kind: WorldEntityKind;
  appearanceKey: string;
  size: number;
  tint?: number;
  label?: string;
}

export interface WorldEntityState {
  x: number;
  y: number;
  rotation?: number;
  scale?: number;
  alpha?: number;
  healthRatio?: number;
  isInvulnerable?: boolean;
}

export interface WorldEntityHandle {
  update(state: WorldEntityState): void;
  destroy(): void;
}

export type WorldRendererMode = "phaser" | "babylon";

export interface WorldRendererProbe {
  renderer: WorldRendererMode;
  ready: boolean;
  phase: string;
  players: number;
  enemies: number;
  abilityEntities: number;
  defenses: number;
  surfaceVeins: number;
  projectiles: number;
  fpsP5: number;
  quality: "low" | "balanced" | "high";
  camera: {
    pitchDegrees: number;
    fovDegrees: number;
    radius: number;
  };
  backend?: {
    vendor: string;
    renderer: string;
    version: string;
    renderWidth: number;
    renderHeight: number;
  };
}

export function resolveWorldRendererMode(
  search: string,
): WorldRendererMode {
  const value = new URLSearchParams(search).get("renderer")?.toLowerCase();
  return value === "3d" || value === "babylon" ? "babylon" : "phaser";
}

export function worldToScenePosition(
  x: number,
  y: number,
  worldWidth: number,
  worldHeight: number,
): { x: number; z: number } {
  return {
    x: x - worldWidth / 2,
    z: y - worldHeight / 2,
  };
}

/** Maps screen-oriented movement to the fixed Warcraft-style Babylon camera. */
export function babylonScreenMovementToWorld(
  movement: Readonly<{ x: number; y: number }>,
): { x: number; y: number } {
  return { x: -movement.x, y: movement.y };
}

/** Frame-rate independent smoothing without a distance-triggered hard jump. */
export function networkMotionSmoothingFactor(
  deltaSeconds: number,
  correctionDistance: number,
): number {
  if (correctionDistance >= 1_400) {
    return 1;
  }
  const rate = Math.min(14, 8 + correctionDistance / 100);
  return 1 - Math.exp(-rate * Math.max(0, deltaSeconds));
}

/** Keeps the complete Warcraft-style camera footprint inside the arena. */
export function clampCameraTargetToWorld(
  x: number,
  y: number,
  worldWidth: number,
  worldHeight: number,
  marginX = 1_100,
  marginY = 950,
): { x: number; y: number } {
  const safeMarginX = Math.min(Math.max(0, marginX), worldWidth / 2);
  const safeMarginY = Math.min(Math.max(0, marginY), worldHeight / 2);
  return {
    x: Math.min(worldWidth - safeMarginX, Math.max(safeMarginX, x)),
    y: Math.min(worldHeight - safeMarginY, Math.max(safeMarginY, y)),
  };
}

const CITY_BUILDING_VISUAL_SIZES: Readonly<Record<string, number>> = {
  "city.building.watchhouse": 60,
  "city.building.muster-hall": 68,
  "city.building.totem-court": 64,
  "city.building.storehouse": 52,
  "city.building.treasure-masonry": 48,
};

/** Visual diameter is intentionally independent from authoritative aggro radius. */
export function resolveCityBuildingVisualSize(
  appearanceKey: string,
  authoritativeRadius: number,
): number {
  return CITY_BUILDING_VISUAL_SIZES[appearanceKey] ?? authoritativeRadius * 1.4;
}
